import {
  ContainerTextContainer,
  CustomXBadge,
  CustomYBadge,
  GraphContainer,
  YAxisTitle,
} from '../BarStacked/BarStacked.styles';
import { ChartToolTip } from '../ChartToolTip';
import {
  BarHorizontalAndAxisContainer,
  BarHorizontalSvgContainer,
  ScrollableDiv,
  XAxisTitle,
} from './BarHorizontal.styles';
import { VirtualElement } from '@popperjs/core';
import * as d3 from 'd3';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { ContainerText } from 'src/components/atoms';
import { useCache, useElementDimensions } from 'src/hooks';
import {
  appendAxis,
  appendAxisAndTicks,
  appendBars,
  chartColors,
  createBandScale,
  createColors,
  createLinearScale,
  createXAxis,
  createYAxis,
} from 'src/utils';

interface Props {
  xTitle: string;
  yTitle: string;
  monocolor?: string;
  data: {
    bar: number;
    label: string;
    value: number;
    percentage?: number;
  }[];
  xAxisMax?: number;
  graphId: string;
  tooltip?: boolean;
  tooltipType: 'substantiation' | 'donut-and-bar-horizontal';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onHover?: (d: any) => void;
  titleDotColor?: string | undefined;
  pdfIsDownloading?: boolean;
  pdfDownloadHeight?: number;
}

export const BarHorizontal = (props: Props) => {
  const theme = useTheme();
  const container = useRef(null);
  const [coords, setCoords] = useState<[number, number] | null>(null);
  const [tooltipData, setTooltipData] = useState<
    (typeof props)['data'][0] | null
  >();
  const graph = useRef(null);
  const cache = useCache(props.data);
  const [hasScroll, setHasScroll] = useState(false);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(svgContainerRef);
  const tooltipHeight = 127;
  const heightAdjustmentForMultipleRows = 30;
  const height = 450;
  const minGraphWidth = 500;
  const labelPadding = 24;
  const yAxisWidth = 10;
  const barHeight = 40;
  const tooltipPadding = 5;
  const { colors } = createColors({
    colorRange: chartColors,
  });
  const tickSize = 25;
  //truncates the axis text when greater than 140px
  const maxTextWidth = 140;
  const extraWidthWhenBold = 5;
  function truncateText(this: SVGTextElement) {
    const self = d3.select(this);
    if (self) {
      let textLength = self?.node()?.getComputedTextLength() ?? maxTextWidth;
      let text = self.text();
      while (
        textLength > maxTextWidth - extraWidthWhenBold &&
        text.length > 0
      ) {
        text = text.slice(0, -1);
        self.text(text + '...');
        textLength = self?.node()?.getComputedTextLength() ?? maxTextWidth;
      }
    }
  }
  const emptyState = props.data.every((item) => item.value === 0);
  const margin = {
    top: 0,
    right: 20,
    bottom: 20,
    left: 25 + maxTextWidth,
  };

  const minGraphHeight = props.pdfIsDownloading
    ? (props.pdfDownloadHeight ?? 500)
    : height - margin.top - margin.bottom;

  const [graphHeight, setGraphHeight] = useState(
    props.data.length * barHeight - margin.top - margin.bottom,
  );
  const AXISLABELWIDTH = 215;
  const x = (d: Props['data'][0]) => Number(d.value);

  const graphWidth = Math.max(containerWidth, minGraphWidth);

  useLayoutEffect(() => {
    const barGraph = d3.select(graph.current);
    const containerSvg = d3.select(container.current);

    if (props.data.length * barHeight < minGraphHeight) {
      setHasScroll(false);
      setGraphHeight(minGraphHeight);
    } else {
      setHasScroll(true);
      setGraphHeight(
        props.data.length * barHeight - margin.top - margin.bottom,
      );
    }

    // Reset the graph
    barGraph.selectAll('*').remove();
    containerSvg.selectAll('*').remove();

    // Construct scales, axes, and formats
    const cacheX = d3.map(cache ?? [], x);
    const bars = d3.map(props.data, (d) => d.label);
    const xMax =
      emptyState || !Number(d3.max(cacheX)) ? 100 : Number(d3.max(cacheX));
    const xDomainMax = props.xAxisMax
      ? props.xAxisMax > 10
        ? Math.ceil(props.xAxisMax / 5) * 5
        : Math.ceil(props.xAxisMax)
      : xMax > 10
        ? Math.ceil(xMax / 5) * 5
        : Math.ceil(xMax);

    const { scale: xScale } = createLinearScale({
      startPosition: margin.left,
      endPosition: graphWidth - margin.right,
      domainMax: xDomainMax,
    });

    const { scale: yScale } = createBandScale({
      startPosition: graphHeight,
      endPosition: margin.top,
      domain: bars,
      paddingRatio: 0.6,
    });
    const xAxisTicks = xScale.ticks(10).filter((t) => Number.isInteger(t));

    const { axis: yAxis } = createYAxis({
      axisScale: yScale as d3.ScaleBand<number>,
      tickSize: 0,
      tickPadding: labelPadding,
      tickSizeOuter: 0,
    });

    const { axis: xAxis } = createXAxis({
      axisScale: xScale,
      tickValues: xAxisTicks,
      tickSize: 0,
      tickPadding: 20,
      tickSizeOuter: 0,
      tickFormat: (d) => String(d),
    });

    // Append y-axis
    appendAxis({
      graph: barGraph,
      transform: `translate(${margin.left}, -${margin.bottom})`,
      axis: yAxis,
      theme,
    });

    // Append x-axis
    appendAxisAndTicks({
      axis: xAxis,
      transform: `translate(0, ${minGraphHeight - 40})`,
      width: -minGraphHeight + margin.top + margin.bottom,
      graph: containerSvg,
      hideAxisLine: true,
      dashAxis: 'y2',
      theme,
    });

    // Append bars
    appendBars({
      graph: barGraph,
      data: props.data,
      bars: bars,
      colors: colors,
      monocolor: props.monocolor,
      linearScale: xScale,
      bandScale: yScale,
      graphId: props.graphId,
      setCoords,
      setTooltipData,
      transform: `translate(0, -${margin.bottom})`,
      onHover: props.onHover,
    });
    barGraph
      .select('g')
      .selectAll('text')
      .data(props.data)
      .attr(
        'class',
        (d) => `${props.graphId}-text ${props.graphId}-text-${d.bar}`,
      );
    barGraph
      .select('g')
      .selectAll('text')
      .html('')
      .append('tspan')
      .data(props.data)
      .text((d) => d.label)
      .each(truncateText)
      .append('svg:title')
      .text((d) => d.label);
  }, [
    graphWidth,
    cache,
    tooltipData,
    props.data,
    props.xAxisMax,
    props.monocolor,
    props.graphId,
    props.onHover,
    minGraphHeight,
    emptyState,
    margin.left,
    margin.right,
    margin.top,
    margin.bottom,
    graphHeight,
    colors,
    theme,
  ]);

  const barHorizontalTotal = props.data
    .map((item) => item.value)
    .reduce((a, b) => a + b, 0);

  return (
    <BarHorizontalAndAxisContainer>
      {emptyState && (
        <ContainerTextContainer
          width={graphWidth + tickSize - AXISLABELWIDTH}
          marginLeft={AXISLABELWIDTH}
        >
          <ContainerText text="No Data" icon={true} />
        </ContainerTextContainer>
      )}
      <GraphContainer>
        <YAxisTitle>
          <CustomYBadge variant="new" text={props.yTitle} capitalize />
        </YAxisTitle>
        <BarHorizontalSvgContainer
          ref={svgContainerRef}
          height={
            props.pdfIsDownloading
              ? `${props.pdfDownloadHeight ?? 500}px`
              : `${height}px`
          }
        >
          <svg
            viewBox={`0 0 ${graphWidth} ${minGraphHeight}`}
            height={`${minGraphHeight}px`}
            width={'100%'}
            preserveAspectRatio="none"
          >
            <g ref={container} />
          </svg>
          <ScrollableDiv
            width={'100%'}
            height={`${
              (props.pdfIsDownloading
                ? (props.pdfDownloadHeight ?? 500)
                : minGraphHeight) -
              2 * margin.bottom
            }px`}
            marginTop={`${
              margin.bottom -
              (props.pdfIsDownloading
                ? (props.pdfDownloadHeight ?? 500)
                : height)
            }px`}
            marginBottom={`${margin.bottom}px`}
            hasScroll={props.pdfIsDownloading ? false : hasScroll}
          >
            <svg
              viewBox={`0 0 ${graphWidth} ${graphHeight}`}
              width={'100%'}
              preserveAspectRatio="none"
              height={`${graphHeight}px`}
            >
              <g ref={graph} />
            </svg>
          </ScrollableDiv>
        </BarHorizontalSvgContainer>
      </GraphContainer>
      <XAxisTitle marginLeft={yAxisWidth + margin.left}>
        <CustomXBadge variant="new" text={props.xTitle} capitalize />
      </XAxisTitle>
      {coords && props.tooltip && tooltipData ? (
        props.tooltipType === 'substantiation' ? (
          <ChartToolTip
            root={
              {
                getBoundingClientRect: () => ({
                  top:
                    coords[1] -
                    tooltipHeight -
                    heightAdjustmentForMultipleRows -
                    tooltipPadding,
                  left: coords[0],
                  width: 0,
                  height: 0,
                }),
              } as VirtualElement
            }
            chartData={{
              title: tooltipData.label,
              titleDotColor: props.titleDotColor,

              data: [
                {
                  text: 'Total reports',
                  counter: `${tooltipData.value}`,
                },
                {
                  text: 'Overall percentage',
                  counter: tooltipData.percentage
                    ? `${tooltipData.percentage} %`
                    : `${Math.round(
                        (tooltipData.value / barHorizontalTotal) * 100,
                      )}%`,
                },
              ],
            }}
          />
        ) : (
          <ChartToolTip
            root={
              {
                getBoundingClientRect: () => ({
                  top: coords[1] - tooltipHeight - tooltipPadding,
                  left: coords[0],
                  width: 0,
                  height: 0,
                }),
              } as VirtualElement
            }
            chartData={{
              title: tooltipData.label,
              data: [
                {
                  dotColor: props.monocolor,
                  text: 'Issue count',
                  counter: `${tooltipData.value} (${Math.round(
                    (tooltipData.value / barHorizontalTotal) * 100,
                  )}%)`,
                },
              ],
            }}
          />
        )
      ) : (
        <div />
      )}
    </BarHorizontalAndAxisContainer>
  );
};
